import axios from 'axios';
import { Formik, FormikHelpers, getIn } from 'formik';
import React, { useState } from 'react';
import {
  Alert, Button, Col, Form, Row,
} from 'react-bootstrap';
import ReactDOM from 'react-dom';
import { isValidationError } from '../../../../structures/responses/axios';
import routes from '../../../../utils/routes';
import RequiredFieldLabel from '../../Partials/RequiredFieldLabel';
import voucherValidation from './validation';

interface CreateVoucherProps {}

interface CreateVoucherRequest {
  code: string;
  name: string;
  description: string;
  max_uses: number | string;
  max_uses_user: number | string;
  discount_amount: number | string;
  active: boolean;
  is_fixed: boolean;
  auto_apply: boolean;
  starts_at: string;
  expires_at: string;
}

const initialValues: CreateVoucherRequest = {
  code: '',
  name: '',
  description: '',
  max_uses: '',
  max_uses_user: '',
  discount_amount: '',
  active: true,
  is_fixed: true,
  auto_apply: false,
  starts_at: '',
  expires_at: '',
};

const CreateVoucher: React.FC<CreateVoucherProps> = () => {
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const onSubmit = (
    values: CreateVoucherRequest,
    { setErrors, resetForm }: FormikHelpers<CreateVoucherRequest>,
  ) => {
    setError('');
    setMessage('');

    const submitValues: CreateVoucherRequest = {
      ...values,
    };

    return axios.post(routes.API_VOUCHER_POST, submitValues, { headers: { Accept: 'application/json' } })
      .then((res) => {
        setMessage('Voucher created successfully.');
        resetForm();
      }).catch((err) => {
        if (isValidationError(err)) {
          setError(err.response.data.message);
          setErrors(err.response.data.errors);
        } else {
          setError('An error occurred while creating the voucher.');
        }
      });
  };

  return (
    <>
      {error ? (
        <Row>
          <Col xs={12}>
            <Alert variant="danger">
              {error}
            </Alert>
          </Col>
        </Row>
      ) : null}

      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={voucherValidation}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12} md={8} lg={6}>
                  <Form.Group controlId="code">
                    <Form.Label className="font-weight-bold small-txt text-muted">
                      <RequiredFieldLabel isInvalid={getIn(touched, 'code') && getIn(errors, 'code')}>
                        Code
                      </RequiredFieldLabel>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Code"
                      value={getIn(values, 'code')}
                      isInvalid={getIn(touched, 'code') && getIn(errors, 'code')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {getIn(errors, 'code')}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group controlId="name">
                    <Form.Label className="font-weight-bold small-txt text-muted">
                      <RequiredFieldLabel isInvalid={getIn(touched, 'name') && getIn(errors, 'name')}>
                        Name
                      </RequiredFieldLabel>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Name"
                      value={getIn(values, 'name')}
                      isInvalid={getIn(touched, 'name') && getIn(errors, 'name')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {getIn(errors, 'name')}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group controlId="description">
                    <Form.Label className="font-weight-bold small-txt text-muted">
                      <RequiredFieldLabel isInvalid={getIn(touched, 'description') && getIn(errors, 'description')}>
                        Description
                      </RequiredFieldLabel>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      as="textarea"
                      placeholder="Description"
                      value={getIn(values, 'description')}
                      isInvalid={getIn(touched, 'description') && getIn(errors, 'description')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                      rows={3}
                    />
                    <Form.Control.Feedback type="invalid">
                      {getIn(errors, 'description')}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Row>
                    <Col xs={12} md={6}>
                      <Form.Group controlId="max_uses">
                        <Form.Label className="font-weight-bold small-txt text-muted">
                          <RequiredFieldLabel isInvalid={getIn(touched, 'max_uses') && getIn(errors, 'max_uses')}>
                            Max Uses
                          </RequiredFieldLabel>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          pattern="[0-9]*"
                          placeholder="Max Uses"
                          value={getIn(values, 'max_uses')}
                          isInvalid={getIn(touched, 'max_uses') && getIn(errors, 'max_uses')}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          {getIn(errors, 'max_uses')}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group controlId="max_uses_user">
                        <Form.Label className="font-weight-bold small-txt text-muted">
                          <RequiredFieldLabel isInvalid={getIn(touched, 'max_uses_user') && getIn(errors, 'max_uses_user')}>
                            Max Uses Per User
                          </RequiredFieldLabel>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          pattern="[0-9]*"
                          placeholder="Max Uses Per User"
                          value={getIn(values, 'max_uses_user')}
                          isInvalid={getIn(touched, 'max_uses_user') && getIn(errors, 'max_uses_user')}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          {getIn(errors, 'max_uses_user')}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group controlId="discount_amount">
                    <Form.Label className="font-weight-bold small-txt text-muted">
                      <RequiredFieldLabel isInvalid={getIn(touched, 'discount_amount') && getIn(errors, 'discount_amount')}>
                        Discount Amount
                      </RequiredFieldLabel>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      pattern="[0-9]*"
                      placeholder="Discount Amount"
                      value={getIn(values, 'discount_amount')}
                      isInvalid={getIn(touched, 'discount_amount') && getIn(errors, 'discount_amount')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {getIn(errors, 'discount_amount')}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Row>
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group controlId="is_fixed">
                        <div className={`d-flex ${getIn(touched, 'is_fixed') && getIn(errors, 'is_fixed') ? 'is-invalid' : ''}`}>
                          <Form.Check
                            name="is_fixed"
                            type="checkbox"
                            label="Fixed Discount"
                            onChange={handleChange}
                            checked={getIn(values, 'is_fixed')}
                          />
                        </div>
                        <Form.Control.Feedback type="invalid">
                          {getIn(touched, 'is_fixed') && getIn(errors, 'is_fixed')}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group controlId="active-1">
                        <div className={`d-flex ${getIn(touched, 'active') && getIn(errors, 'active') ? 'is-invalid' : ''}`}>
                          <Form.Check
                            name="active"
                            type="checkbox"
                            label="Active"
                            onChange={handleChange}
                            checked={getIn(values, 'active')}
                          />
                        </div>
                        <Form.Control.Feedback type="invalid">
                          {getIn(touched, 'active') && getIn(errors, 'active')}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group controlId="auto_apply">
                        <div className={`d-flex ${getIn(touched, 'auto_apply') && getIn(errors, 'auto_apply') ? 'is-invalid' : ''}`}>
                          <Form.Check
                            name="auto_apply"
                            type="checkbox"
                            label="Auto Apply"
                            onChange={handleChange}
                            checked={getIn(values, 'auto_apply')}
                          />
                        </div>
                        <Form.Control.Feedback type="invalid">
                          {getIn(touched, 'auto_apply') && getIn(errors, 'auto_apply')}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} md={6}>
                      <Form.Group controlId="starts_at">
                        <Form.Label className="font-weight-bold small-txt text-muted">
                          <RequiredFieldLabel isInvalid={getIn(touched, 'starts_at') && getIn(errors, 'starts_at')}>
                            Starts At
                          </RequiredFieldLabel>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          placeholder="Starts At"
                          value={getIn(values, 'starts_at')}
                          isInvalid={getIn(touched, 'starts_at') && getIn(errors, 'starts_at')}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required
                        />
                        <Form.Text className="text-muted">
                          Voucher will be available at the beginning of this day.
                        </Form.Text>
                        <Form.Control.Feedback type="invalid">
                          {getIn(errors, 'starts_at')}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group controlId="expires_at">
                        <Form.Label className="font-weight-bold small-txt text-muted">
                          <RequiredFieldLabel isInvalid={getIn(touched, 'expires_at') && getIn(errors, 'expires_at')}>
                            Expires At
                          </RequiredFieldLabel>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          placeholder="Expires At"
                          value={getIn(values, 'expires_at')}
                          isInvalid={getIn(touched, 'expires_at') && getIn(errors, 'expires_at')}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required
                        />
                        <Form.Text className="text-muted">
                          Voucher will expire at the end of this day.
                        </Form.Text>
                        <Form.Control.Feedback type="invalid">
                          {getIn(errors, 'expires_at')}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col xs={12}>
                  <Button
                    variant="primary"
                    disabled={isSubmitting}
                    size="lg"
                    type="submit"
                  >
                    Create Voucher
                    {
                      isSubmitting && (
                        <span className="spinner-border spinner-border-sm my-auto ml-3">
                          <span className="sr-only">Loading...</span>
                        </span>
                      )
                    }
                  </Button>
                </Col>
              </Row>
            </form>
          );
        }}
      </Formik>

      {message ? (
        <Row className="mt-4">
          <Col xs={12}>
            <Alert variant="success" className="mb-0  ">
              {message}
            </Alert>
          </Col>
        </Row>
      ) : null}
    </>
  );
};

export default CreateVoucher;

document.querySelectorAll('.react-create-voucher-form').forEach((element) => {
  ReactDOM.render(<CreateVoucher />, element);
});
